'use client';

import type { SignaturePadProps } from './SignaturePad.types';
import { type FC, useState, useContext } from 'react';
import { saveSignature } from '@/_api/client-questionnaires';
import {
  Input,
  Text,
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  Pill,
} from '@/_components';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { useRouter } from 'next/navigation';
import { usePrint } from '@/_hooks/usePrint';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';

export const SignaturePad: FC<SignaturePadProps> = ({
  isLoading,
  requiredSignature,
  onSigned,
  setLoading,
}) => {
  const [signature, setSignature] = useState<string>('');

  const { push } = useRouter();
  const { addToast } = useContext(ToastContext);
  const { fullName, token, isCompanyReadOnly, isUserReadOnly } =
    useContext(UserContext);
  const { selectedQuestionnaire, selectedSection } = useContext(
    ClientQuestionnaireContext,
  );
  const { firm, preferences } = useContext(FirmSettingsContext);
  const { print } = usePrint(
    firm?.name && selectedSection?.name
      ? `${firm?.name} - ${selectedSection?.name}`
      : '',
  );

  const isSignDisabled: boolean = !signature?.length || isLoading;
  const isInputDisabled: boolean =
    isUserReadOnly || isCompanyReadOnly || isLoading;
  const [openPrintDialog, setOpenPrintDialog] = useState<boolean>(false);

  const onSignatureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    return setSignature(event.target.value);
  };

  const onSign = async () => {
    try {
      setLoading(true);
      await saveSignature({
        questionnaireId: selectedQuestionnaire?.id!,
        companyId: selectedQuestionnaire?.company_id!,
        signature,
        signatureId: requiredSignature?.id!,
        token,
      });

      await onSigned();
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
    }
  };

  const onPrint = () => {
    print();
  };

  const onTogglePrintDialog = () => setOpenPrintDialog((previous) => !previous);

  const onOpenMessage = () => push('/inbox');

  return (
    <>
      <Dialog
        isOpen={openPrintDialog}
        title="Print and Sign"
        isWide={false}
        onClose={onTogglePrintDialog}
      >
        <Dialog.Content>
          <Flex gap={8} isColumn={true}>
            <Flex gap={4} isYCentered={true}>
              <Pill label="1" isRound={true} intent="info" />
              <Text>
                Please click the &apos;Print&apos; button below to print the
                engagement letter.
              </Text>
            </Flex>
            <Flex gap={4} isYCentered={true}>
              <Pill label="2" isRound={true} intent="info" />
              <Text>Sign the printed sheet at the end.</Text>
            </Flex>
            <Flex gap={4} isYCentered={true}>
              <Pill label="3" isRound={true} intent="info" />
              <Text>Take a picture or scan the document.</Text>
            </Flex>
            <Flex gap={4} isYCentered={true}>
              <Pill label="4" isRound={true} intent="info" />
              <Text>Upload in conversation.</Text>
            </Flex>
            <ButtonGroup align="right">
              <Button
                value="Go to Conversation"
                intent="secondary"
                onClick={onOpenMessage}
              />
              <Button
                value="Print"
                intent="primary"
                onClick={onPrint}
                iconLeft="printer"
              />
            </ButtonGroup>
          </Flex>
        </Dialog.Content>
      </Dialog>
      <Input
        label="Signature"
        isDisabled={isInputDisabled}
        isLabelHidden={true}
        value={signature}
        data-hide-on-print="true"
        placeholder={fullName}
        onChange={onSignatureChange}
      />
      <div data-hide-on-print="true" className="flex flex-y-center">
        <Text type="small" textAlign="center" color="secondary">
          By signing, I confirm the electronic representation of my signature.
          <br />
          {preferences?.hide_signature_print_option ? null : (
            <Text textAlign="center" type="small" color="secondary">
              <a onClick={onTogglePrintDialog}>
                Click here to opt out and print
              </a>
            </Text>
          )}
        </Text>
      </div>
      <ButtonGroup isFullWidth={true}>
        <Button
          value="Sign"
          intent="primary"
          onClick={onSign}
          iconLeft="nib"
          isLoading={isLoading}
          isDisabled={isSignDisabled}
        />
      </ButtonGroup>
    </>
  );
};
